import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { handleExtendTimeFilterValue, formatFilterSaveValue } from '@intelligence/utils/filterUtils';
import { Card } from 'tea-component';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import { handleRangeMiniGameMoreFilterExtend } from '@intelligence/utils/dateUtils';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import {
  // 时间筛选器
  RANGE_TIME as FILTER_RANGE_TIME,
  // 品类筛选器
  GENRETOR as FILTER_GENRE_SELECT,
  MY_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, FILTER_DEFAULT_VALUE } from '../../config';
import './index.scss';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IMobileStoreRankTopFilter, IStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';
import useFilterUseHook from '@/pages/intelligence/hooks/useFilterUseHook';
import WeixinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';

interface IMobileStoreRankTopFilterProps extends IBaseProps {
  isOperate: boolean;
  morePagePath?: string;
  morePageSource?: string;
  filter: IStoreRankTopFilter | IMobileStoreRankTopFilter;
  onConfirm: (payload: IMobileStoreRankTopFilter) => void;
  onReset: () => void;
  rangeTimeComponentDefault: any;
}

const MobileStoreRankTopFilter: React.FC<IMobileStoreRankTopFilterProps> = (props) => {
  const { style, className, isOperate, filter, onConfirm, onReset, rangeTimeComponentDefault, entityType } = props;
  // console.debug('MobileStoreRankTopFilter', props)

  const { filterExtend: filterExtendTmp, setFilterExtend, resetFilterExtend } = useFilterExtend({});
  const initialFormValue = (() => {
    if (!isOperate) return FILTER_DEFAULT_VALUE;
    const filterExtend = handleRangeMiniGameMoreFilterExtend(filterExtendTmp);

    const rangeTimeFilterExtend = filterExtend[FILTER_RANGE_TIME];
    const genreSelectFilterExtend = filterExtend[FILTER_GENRE_SELECT];

    const rangeTimeDefault = FILTER_DEFAULT_VALUE[FILTER_RANGE_TIME];
    const genreSelectDefault = FILTER_DEFAULT_VALUE[FILTER_GENRE_SELECT];
    const myFilterFilterExtend = filterExtend?.[MY_FILTER]?.value;
    return {
      [MY_FILTER]: {
        value: myFilterFilterExtend ?? '',
      },
      [FILTER_RANGE_TIME]: {
        type: 'daily', // rangeTimeFilterExtend?.filterType ?? rangeTimeDefault?.type,
        value: handleExtendTimeFilterValue({
          extendFilterValues: rangeTimeFilterExtend,
          defaultValue: rangeTimeDefault?.value,
        }),
        componentDefault: rangeTimeComponentDefault,
      },
      [FILTER_GENRE_SELECT]: {
        source: 'wechat_minigame',
        mainGenres: genreSelectFilterExtend?.genretor?.mainGenres ?? genreSelectDefault?.mainGenres,
        subGenres: genreSelectFilterExtend?.genretor?.subGenres ?? genreSelectDefault?.subGenres,
        minigame_genre: genreSelectFilterExtend?.genretor?.minigame_genre ?? genreSelectDefault?.minigame_genre,
      },
    };
  })();

  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    handleGenresChange,
    handleReset: handleGenresReset,
  } = useGenreComponent(props, undefined, { defaultGenreSource: GENRE_SOURCE_MAP.MINIGAME });

  const [inputList, setInputList] = useState(FILTER_DEFAULT_INPUT_LIST);
  const [formValue, setFormValue] = useState(initialFormValue);

  const formatFormValue = (values: any) => {
    const newFilterValues = formatFilterSaveValue({
      values,
      timeConfig: {
        // 天粒度单选1天
        needSameDay: true,
      },
      selectRegionConfig: {
        // 单选的国家筛选器
        isSingleCountry: true,
      },
    });
    return newFilterValues;
  };

  const {
    filterName,
    filterData,
    updateFormValue,
    onSaveFilterMsg,
    onFilterChange,
  } = useFilterUseHook({
    defaultValue: FILTER_DEFAULT_VALUE,
    formValue,
    setFormValue,
    timeFilterValues: {
      type: 'daily',
      value: [filter?.start_date, filter?.end_date]
    },
    formatFormValue,
  });


  const handleConfirm = (value: any, apiValue: any) => {
    // console.debug('handleConfirmhandleConfirm', value);
    // console.debug('handleConfirmhandleConfirmapiValueapiValue', apiValue);
    setFormValue(value);
    const filterForm = {
      start_date: apiValue[FILTER_RANGE_TIME]?.start_date,
      end_date: apiValue[FILTER_RANGE_TIME]?.end_date,
      genre_source: apiValue[FILTER_GENRE_SELECT]?.genre_source,
      minigame_genre: apiValue[FILTER_GENRE_SELECT]?.minigame_genre || [],
    } as any;

    onConfirm?.(filterForm);
    // 点击确认后，将筛选器保存方案及保存数据存到缓存中
    onSaveFilterMsg(value, apiValue);
    // 榜单国家筛选器在confirm过滤继承值后需要更新继承值
    // feat国家筛选闪动问题,setFilterExtend放在后面,
    setFilterExtend(apiValue);
  };

  const handleReset = () => {
    const newFormValue = {
      ...FILTER_DEFAULT_VALUE,
      [FILTER_RANGE_TIME]: {
        type: 'date',
        value: rangeTimeComponentDefault?.date
      },
    };
    setFormValue(newFormValue);
    genreRef.current?.reset();
    resetFilterExtend();
    onReset?.();
  };

  /** 时间筛选器值由父组件更新 */
  useEffect(() => {
    updateFormValue();
  }, [
    filterName,
    JSON.stringify(filterData),
    filter?.start_date, filter?.end_date,
  ]);


  useEffect(() => {
    const inputListTmp = [...inputList];
    const genreSelectTmp = inputListTmp.find((item) => item?.key === FILTER_GENRE_SELECT) as any;

    if (genreSelectTmp) {
      genreSelectTmp.genreData = {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        handleGenresChange,
        handleGenresReset,
      };
      genreSelectTmp.sources = ['wechat_minigame'];
      genreSelectTmp.entityType = entityType;
      genreSelectTmp.overlayClassName = 'weixin-mode';
      genreSelectTmp.tipTextOutside = {
        remarks: (
          <div>
            Data of Genre comes from the <img className="dbi-genre-select-tip-text-remark-img" src={WeixinIcon} alt="" />
            WeChat Mini Game leaderboard on the Gravity Engine.
          </div>
        ),
        remarksCN: (
          <div>
            品类数据来源于第三方平台引力引擎的<img className="dbi-genre-select-tip-text-remark-img" src={WeixinIcon} alt="" />
            微信小游戏榜单。
          </div>
        ),
      };
    }
    setInputList(inputListTmp);
  }, [genreRef, genreSource, mainGenres, subGenres, handleGenresChange, handleGenresReset]);

  return (
    <Card style={style} className={classnames(className)}>
      <Card.Body>
        <Filter
          showButton={true}
          inputList={inputList}
          defaultValue={{
            ...FILTER_DEFAULT_VALUE,
            [FILTER_RANGE_TIME]: {
              type: 'date',
              value: rangeTimeComponentDefault?.date
            },
          }}
          value={formValue}
          onConfirm={handleConfirm}
          onReset={handleReset}
          onChange={onFilterChange}
          isHaveFilterSave={true}
        />
      </Card.Body>
    </Card>
  );
};

export default MobileStoreRankTopFilter;
